import React, { useState, useContext, useEffect } from 'react';
import { ListContactType } from '@bb/contacts';

import { Button } from 'components';
import {
  AuthContext,
  ContactDrawerContext,
  ContactDrawerMode,
  ContactDrawerType,
} from 'contexts';
import { LayoutCard, Loading } from '../components';
import useContacts from './useContacts';
import useBrokerageUsers from './useBrokerageUsers';
import ContactCardContent from './ContactCardContent';
import BrokerageCardContent from './BrokerageCardContent';
import { TabKeyType } from './types';

type TabList = {
  key: TabKeyType;
  tab: React.ReactNode;
};

const ContactsCard = () => {
  const { hasFeature } = useContext(AuthContext);

  const [tabList, setTabList] = useState<TabList[]>([
    {
      key: TabKeyType.Client,
      tab: <span data-cy="contacts-tab-clients">Clients</span>,
    },
    {
      key: TabKeyType.Supplier,
      tab: <span data-cy="contacts-tab-suppliers">Suppliers</span>,
    },
  ]);
  const { openContactDrawer } = useContext(ContactDrawerContext);
  const [activeTabKey, setActiveTabKey] = useState(TabKeyType.Client);

  const {
    isLoading: isClientLoading,
    contacts: clients,
    searchValue: clientSearchValue,
    setSearchValue: setClientSearchValue,
  } = useContacts(ListContactType.Client);
  const {
    isLoading: isSupplierLoading,
    contacts: suppliers,
    searchValue: supplierSearchValue,
    setSearchValue: setSupplierSearchValue,
  } = useContacts(ListContactType.Supplier);
  const {
    isLoading: isBrokerageLoading,
    users,
    searchValue: brokerageSearchValue,
    setSearchValue: setBrokerageSearchValue,
  } = useBrokerageUsers();

  let content: React.ReactNode;
  let isLoading: boolean;
  let searchValue: string;
  let setSearchValue: (value: string) => void;
  if (activeTabKey === TabKeyType.Brokerage) {
    content = <BrokerageCardContent users={users} />;
    isLoading = isBrokerageLoading;
    searchValue = brokerageSearchValue;
    setSearchValue = setBrokerageSearchValue;
  } else if (activeTabKey === TabKeyType.Client) {
    content = <ContactCardContent contacts={clients} />;
    isLoading = isClientLoading;
    searchValue = clientSearchValue;
    setSearchValue = setClientSearchValue;
  } else {
    content = <ContactCardContent contacts={suppliers} />;
    isLoading = isSupplierLoading;
    searchValue = supplierSearchValue;
    setSearchValue = setSupplierSearchValue;
  }

  useEffect(() => {
    if (
      hasFeature('MY_BROKERAGE') &&
      !tabList.map(tab => tab.key).includes(TabKeyType.Brokerage)
    ) {
      setTabList(prevTabList => [
        ...prevTabList,
        {
          key: TabKeyType.Brokerage,
          tab: <span data-cy="contacts-tab-my-brokerage">My Brokerage</span>,
        },
      ]);
    } else if (
      !hasFeature('MY_BROKERAGE') &&
      tabList.map(tab => tab.key).includes(TabKeyType.Brokerage)
    ) {
      setTabList(prevTabList =>
        prevTabList.filter(tab => tab.key !== TabKeyType.Brokerage),
      );
    }
  }, [hasFeature, tabList]);

  return (
    <LayoutCard
      activeTabKey={activeTabKey}
      hideViewAll
      onTabChange={key => setActiveTabKey(key as TabKeyType)}
      search={{ searchValue, onSearchValueChange: setSearchValue }}
      tabList={tabList}
      tabProps={{
        size: 'small',
      }}
      titleLink={{
        title: <span data-cy="view-all-contacts">Contacts</span>,
        linkPath: '/contacts/client',
      }}
      titleExtra={
        <Button
          size="small"
          onClick={() =>
            openContactDrawer(ContactDrawerMode.Add, ContactDrawerType.Contact)
          }
          data-cy="add-contact-button"
        >
          Add
        </Button>
      }
      data-cy="dashboard-contacts-card"
    >
      {isLoading ? <Loading /> : content}
    </LayoutCard>
  );
};

export default ContactsCard;
